import React, { useRef, useEffect } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

const ReusableTrendsGraph = ({ 
  labels = ["00:00", "02:00", "04:00", "06:00", "12:00", "18:00", "24:00", "Now"],
  totalData = [],
  failedData = [],
  totalLabel = "Total Runs",
  failedLabel = "Failed Runs",
  height = "200px",
  maxValue = 320,
  stepSize = 50,
  showLegend = false,
  totalColor = "#29b6f6",
  failedColor = "#ef5350"
}) => {
  const chartRef = useRef(null);

  const chartData = {
    labels: labels,
    datasets: [
      {
        label: totalLabel,
        data: totalData,
        borderColor: totalColor,
        backgroundColor: `rgba(41,182,246,0.15)`,
        fill: true,
        tension: 0.4,
        pointBackgroundColor: "#ffffff",
        pointBorderColor: totalColor,
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        borderWidth: 2,
      },
      {
        label: failedLabel,
        data: failedData,
        borderColor: failedColor,
        backgroundColor: `rgba(239, 120, 100, 0.15)`,
        fill: true,
        tension: 0.4,
        pointBackgroundColor: "#ffffff",
        pointBorderColor: failedColor,
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        borderWidth: 2,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 1000,
      easing: "easeInOutQuart",
    },
    clip: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { display: showLegend },
      tooltip: {
        backgroundColor: "rgba(255,255,255,0.97)",
        titleColor: "#111827",
        bodyColor: "#4b5563",
        borderColor: "#e5e7eb",
        borderWidth: 1,
        padding: 10,
        cornerRadius: 2,
      },
    },
    scales: {
      x: {
        grid: { color: "rgba(0,0,0,0.06)", drawTicks: false },
        border: { dash: [4, 4] },
        ticks: { color: "#9ca3af", font: { size: 10 }, autoSkip: false, maxRotation: 0 },
      },
      y: {
        min: 0,
        max: maxValue,
        ticks: { stepSize: stepSize, color: "#9ca3af", font: { size: 10 } },
        grid: { color: "rgba(0,0,0,0.05)", drawTicks: false },
        border: { display: false },
      },
    },
  };

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    const ctx = chart.ctx;
    const chartArea = chart.chartArea;
    
    if (!chartArea) return;

    // Create gradient for Total Runs (blue)
    const totalGradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
    totalGradient.addColorStop(0, 'rgba(41, 182, 246, 0.4)');  // More opaque at top
    totalGradient.addColorStop(0.5, 'rgba(41, 182, 246, 0.2)'); // Medium opacity
    totalGradient.addColorStop(1, 'rgba(41, 182, 246, 0.05)'); // Nearly transparent at bottom

    // Create gradient for Failed Runs (red/pink)
    const failedGradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
    failedGradient.addColorStop(0, 'rgba(239, 83, 80, 0.35)');  // More opaque at top
    failedGradient.addColorStop(0.5, 'rgba(239, 83, 80, 0.15)'); // Medium opacity
    failedGradient.addColorStop(1, 'rgba(239, 83, 80, 0.03)');  // Nearly transparent at bottom

    chart.data.datasets[0].backgroundColor = totalGradient;
    chart.data.datasets[1].backgroundColor = failedGradient;
    chart.update("none");
  }, [totalColor, failedColor]);

  return (
    <div style={{ position: "relative", height: height, width: "100%" }}>
      <Line ref={chartRef} data={chartData} options={chartOptions} />
    </div>
  );
};

export default ReusableTrendsGraph;
